<script setup lang="ts">
import { ref, toRef } from 'vue';
import type { TComplianceSummary } from '@/_core/entities/report/models/types';
import SearchInput from '@/_core/shared/ui/SearchInput/SearchInput.vue';
import { useComplianceFilter } from './useComplianceFilter';
import { useClickOutside } from './useClickOutside';

const props = defineProps<{
  summary?: TComplianceSummary;
  selectedRequirementId?: string;
  selectedItemId?: string;
}>();

const emit = defineEmits<{
  (e: 'select', payload: { requirementId?: string; itemId?: string }): void;
  (e: 'clear'): void;
}>();

const isOpen = ref(false);
const rootRef = ref<HTMLElement | null>(null);

const { search, filteredGroups, selectedCount } = useComplianceFilter({
  summary: toRef(props, 'summary'),
  selectedRequirementId: toRef(props, 'selectedRequirementId'),
  selectedItemId: toRef(props, 'selectedItemId'),
  onClear: () => emit('clear'),
});

useClickOutside(rootRef, () => {
  isOpen.value = false;
});

function selectItem(itemId: string) {
  emit('select', { itemId });
}

function clearAll() {
  emit('clear');
  search.value = '';
}
</script>

<template>
  <div ref="rootRef" class="tw:relative tw:w-72">
    <SearchInput
      v-model="search"
      :placeholder="selectedCount > 0 ? 'Compliance filter' : 'Search compliance documents'"
      @focus="isOpen = true"
      @click="isOpen = true"
    >
      <template #actions>
        <button
          type="button"
          class="tw:shrink-0 tw:text-secondary tw:p-1 tw:transition-transform"
          :class="{ 'tw:rotate-180': isOpen }"
          :aria-label="isOpen ? 'Close' : 'Open'"
          @click.stop="isOpen = !isOpen"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      </template>
    </SearchInput>

    <div
      v-if="isOpen"
      class="tw:absolute tw:right-0 tw:top-full tw:mt-1 tw:w-full tw:bg-white tw:border tw:border-black/10 tw:rounded-md tw:shadow-lg tw:z-20 tw:flex tw:flex-col"
    >
      <div class="tw:max-h-80 tw:overflow-y-auto tw:py-2">
        <div
          v-if="filteredGroups.length === 0"
          class="tw:px-4 tw:py-3 tw:text-secondary tw:typography-small"
        >
          No matches.
        </div>
        <div
          v-for="(req, idx) in filteredGroups"
          :key="req.id"
          :class="{ 'tw:border-t tw:border-black/5': idx > 0 }"
        >
          <div class="tw:px-4 tw:pt-3 tw:pb-1 tw:typography-small tw:text-secondary">
            {{ req.name }}
          </div>
          <button
            v-for="item in req.items"
            :key="item.id"
            type="button"
            class="tw:w-full tw:text-left tw:pl-8 tw:pr-4 tw:py-2 tw:typography-small tw:hover:bg-black/5"
            :class="{ 'tw:text-main-blue': props.selectedItemId === item.id }"
            @click="selectItem(item.id)"
          >
            {{ item.itemId }}
          </button>
        </div>
      </div>

      <div
        class="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:px-4 tw:py-2 tw:border-t tw:border-black/5 tw:typography-small"
      >
        <span class="tw:text-secondary">
          Selected
          <span class="tw:inline-block tw:ml-1 tw:px-1.5 tw:rounded-full tw:bg-black/10">
            {{ selectedCount }}
          </span>
        </span>
        <button
          type="button"
          class="tw:text-main-blue"
          :disabled="selectedCount === 0"
          :class="{ 'tw:opacity-50 tw:cursor-not-allowed': selectedCount === 0 }"
          @click="clearAll"
        >
          Clear All
        </button>
      </div>
    </div>
  </div>
</template>
